import React, { useEffect, useState } from 'react'
import moment from 'moment'
import API from '../../services/API'
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import PhonelinkEraseIcon from '@mui/icons-material/PhonelinkErase'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

// Internal tester logins for the test tenant (devops) — registry-DB
// TestAccounts with DELIBERATELY plaintext passwords, shown and edited here.
// Each account signs in as exactly one seeded user; changing user means
// logging out and in with a different account (no persona switching, no
// public entry). The reset button wipes the tenant back to the demo seed.

const lastActivityColour = (value) => {
  if (!value) return 'text.disabled'
  const daysAgo = moment().diff(moment(value), 'days')
  if (daysAgo <= 2) return 'anGreen.main'
  if (daysAgo <= 7) return 'anOrange.main'
  return 'anRed.main'
}

const userOptionLabel = (user) =>
  [
    user.name,
    user.role ?? user.kind,
    user.persona ? `persona: ${user.persona}` : null,
    user.isSiteAdmin ? 'site admin' : null,
  ]
    .filter(Boolean)
    .join(' · ')

const Toolbar = () => (
  <Stack direction="row" justifyContent="flex-end" padding={1}>
    <GridToolbarQuickFilter />
  </Stack>
)

const emptyForm = {
  email: '',
  password: '',
  user: null,
  notes: '',
  isActive: true,
}

const TestAccounts = () => {
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [tenantUsers, setTenantUsers] = useState([])
  const [tenantName, setTenantName] = useState('')
  const [usersError, setUsersError] = useState('')

  // dialog = { mode: 'create' } | { mode: 'edit', id }
  const [dialog, setDialog] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [dialogError, setDialogError] = useState('')
  const [resetting, setResetting] = useState(false)

  const loadAccounts = () => {
    API.get('/admin/test-accounts')
      .then((res) => {
        if (res.status === 200) {
          setAccounts(res.data)
        }
      })
      .finally(() => setLoading(false))
  }

  const loadTenantUsers = () => {
    API.get('/admin/test-tenants/users').then((res) => {
      if (res.status === 200) {
        setTenantUsers(res.data.users ?? [])
        setTenantName(res.data.tenant?.subdomain ?? '')
        setUsersError('')
      } else {
        setUsersError(
          res.data?.error ??
            'No test tenant found — run provision-test-tenant.mjs first.',
        )
      }
    })
  }

  useEffect(() => {
    loadAccounts()
    loadTenantUsers()
  }, [])

  const openCreate = () => {
    setForm(emptyForm)
    setDialogError('')
    setDialog({ mode: 'create' })
  }

  const openEdit = (row) => {
    setForm({
      email: row.email ?? '',
      password: row.password ?? '',
      user:
        tenantUsers.find((user) => String(user._id) === String(row.user)) ??
        (row.user ? { _id: row.user, name: row.userLabel ?? 'Unknown user' } : null),
      notes: row.notes ?? '',
      isActive: row.isActive !== false,
    })
    setDialogError('')
    setDialog({ mode: 'edit', id: row._id })
  }

  const setField = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }))

  const handleSave = async () => {
    setDialogError('')

    if (!form.email) {
      setDialogError('An email address is required.')
      return
    }
    if (!form.user?._id) {
      setDialogError('Pick the seeded user this account signs in as.')
      return
    }
    if (form.password.length < 8) {
      setDialogError('The password must be at least 8 characters.')
      return
    }

    const body = {
      email: form.email,
      password: form.password,
      user: form.user._id,
      userLabel: form.user.name
        ? userOptionLabel(form.user)
        : (form.user.label ?? ''),
      notes: form.notes,
    }

    setSaving(true)
    let res
    if (dialog.mode === 'create') {
      res = await API.post('/admin/test-accounts', body)
    } else {
      res = await API.patch(`/admin/test-accounts/${dialog.id}`, {
        ...body,
        isActive: form.isActive,
      })
    }
    setSaving(false)

    if (res.status !== 200 && res.status !== 201) {
      setDialogError(res.data?.error ?? 'Failed to save the test account')
      return
    }

    setDialog(null)
    loadAccounts()
  }

  const handleKillSessions = async (row) => {
    if (!window.confirm(`Log ${row.email} out of all their sessions?`)) return

    const res = await API.post(`/admin/test-accounts/${row._id}/kill-sessions`)
    if (res.status !== 200) {
      window.alert(res.data?.error ?? 'Failed to kill sessions')
      return
    }
    window.alert(
      `Killed ${res.data?.killed ?? 0} active session${
        res.data?.killed === 1 ? '' : 's'
      }.`,
    )
  }

  // Same validated hydrate path as the demo reset — a failure leaves the
  // current data intact. Unlike the demo there is NO nightly reset, so this
  // button is the only thing that wipes the test tenant.
  const handleResetTestTenant = async () => {
    if (
      !window.confirm(
        'Reset the test tenant now? ALL its data — including anything the team built while testing — is wiped and reloaded from the demo seed.',
      )
    )
      return

    setResetting(true)
    const res = await API.post('/admin/test-tenants/reset')
    setResetting(false)

    if (res.status !== 200) {
      window.alert(
        `Reset failed — the existing data has been left as it was.\n\n${
          res.data?.error ?? 'Unknown error'
        }`,
      )
      return
    }
    const results = res.data?.results ?? []
    if (!results.length) {
      window.alert(
        'No test tenants were found to reset (Tenant.isTestTenant — run provision-test-tenant.mjs).',
      )
      return
    }
    window.alert(
      results
        .map(
          (r) =>
            `${r.tenant}: ${r.documents} records across ${r.models} models in ${r.seconds}s${
              r.cqcRecomputed ? '' : ' (CQC Centre recompute failed)'
            }`,
        )
        .join('\n'),
    )
  }

  const handleDelete = async (row) => {
    if (
      !window.confirm(
        `Delete the test account "${row.email}"? Its sessions will be killed immediately.`,
      )
    )
      return

    const res = await API.delete(`/admin/test-accounts/${row._id}`)
    if (res.status !== 200) {
      window.alert(res.data?.error ?? 'Failed to delete the test account')
      return
    }
    loadAccounts()
  }

  return (
    <Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Stack>
          <Typography variant="title2">Test Accounts</Typography>
          <Typography variant="caption" color="text.secondary">
            Internal logins for the {tenantName || 'devops'} test tenant.
            Passwords are stored in plain text on purpose — never reuse a real
            one.
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Wipe the test tenant and reload the full demo seed. Unlike the demo there is no nightly reset — this button is the only thing that wipes it.">
            <span>
              <Button
                variant="outlined"
                color="error"
                startIcon={<RestartAltIcon />}
                onClick={handleResetTestTenant}
                disabled={resetting}
              >
                {resetting ? 'Resetting…' : 'Reset Test Tenant'}
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
          >
            New Test Account
          </Button>
        </Stack>
      </Stack>
      {usersError && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {usersError}
        </Alert>
      )}
      <DataGrid
        rows={accounts}
        loading={loading}
        initialState={{
          sorting: {
            sortModel: [{ field: 'createdAt', sort: 'desc' }],
          },
        }}
        columns={[
          { field: 'email', headerName: 'Email', minWidth: 200, flex: 1 },
          {
            field: 'password',
            headerName: 'Password',
            minWidth: 160,
            flex: 0.7,
            renderCell: ({ value }) => (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {value}
                </Typography>
                <Tooltip title="Copy password">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      navigator.clipboard?.writeText(value ?? '')
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            ),
          },
          {
            field: 'userLabel',
            headerName: 'Signs In As',
            minWidth: 220,
            flex: 1.2,
            renderCell: ({ value }) => value || '—',
          },
          {
            field: 'status',
            headerName: 'Status',
            minWidth: 100,
            flex: 0.4,
            valueGetter: (value, row) =>
              row.isActive === false ? 'Disabled' : 'Active',
            renderCell: ({ row }) =>
              row.isActive === false ? (
                <Chip
                  label="Disabled"
                  variant="outlined"
                  size="small"
                  sx={{ borderRadius: 1 }}
                />
              ) : (
                <Chip
                  label="Active"
                  color="anGreen"
                  size="small"
                  sx={{ borderRadius: 1 }}
                />
              ),
          },
          {
            field: 'lastActive',
            headerName: 'Last Active',
            minWidth: 120,
            flex: 0.6,
            valueGetter: (value) => (value ? new Date(value) : null),
            sortComparator: (a, b) => (a?.getTime() ?? 0) - (b?.getTime() ?? 0),
            renderCell: ({ value }) => (
              <Typography
                variant="body2"
                sx={{ color: lastActivityColour(value) }}
              >
                {value ? moment(value).fromNow() : 'Never'}
              </Typography>
            ),
          },
          {
            field: 'createdAt',
            headerName: 'Created',
            minWidth: 140,
            flex: 0.6,
            valueGetter: (value) => (value ? new Date(value) : null),
            sortComparator: (a, b) => (a?.getTime() ?? 0) - (b?.getTime() ?? 0),
            renderCell: ({ value, row }) => (
              <Stack>
                <Typography variant="body2">
                  {value ? moment(value).format('Do MMM YYYY') : '—'}
                </Typography>
                {row.createdBy && (
                  <Typography variant="caption" color="text.secondary">
                    by {row.createdBy.firstName} {row.createdBy.lastName}
                  </Typography>
                )}
              </Stack>
            ),
          },
          {
            field: 'actions',
            headerName: '',
            minWidth: 130,
            sortable: false,
            filterable: false,
            disableExport: true,
            align: 'right',
            renderCell: ({ row }) => (
              <Stack direction="row">
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => openEdit(row)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Kill sessions">
                  <IconButton
                    size="small"
                    onClick={() => handleKillSessions(row)}
                  >
                    <PhonelinkEraseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => handleDelete(row)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ),
          },
        ]}
        getRowId={(row) => row._id}
        disableRowSelectionOnClick
        onRowClick={({ row }) => openEdit(row)}
        slots={{ toolbar: Toolbar }}
        sx={{
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            whiteSpace: 'normal',
            lineHeight: 'normal',
            textAlign: 'center',
          },
          '.MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
          },
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
          border: 0,
          '& .MuiDataGrid-toolbar': {
            border: 'none',
          },
          '& .MuiDataGrid-main': {
            border: 2,
            borderColor: 'primary.main',
            borderRadius: '0px',
          },
          '& .MuiDataGrid-sortIcon': {
            opacity: 1,
            color: 'primary.contrastText',
          },
          '& .MuiDataGrid-menuIconButton': {
            opacity: 1,
            color: 'primary.contrastText',
          },
          '& .MuiDataGrid-columnHeaders': {
            borderRadius: '0px 0px 0 0',
          },
        }}
      />

      <Dialog
        open={!!dialog}
        onClose={() => setDialog(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {dialog?.mode === 'create' ? 'New Test Account' : 'Edit Test Account'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Email address"
              type="email"
              value={form.email}
              onChange={setField('email')}
              fullWidth
              required
            />
            <TextField
              label="Password (stored in plain text)"
              value={form.password}
              onChange={setField('password')}
              fullWidth
              required
              helperText="Minimum 8 characters. Visible to every CRM admin — never reuse a real password."
            />
            <Autocomplete
              options={tenantUsers}
              value={form.user}
              onChange={(e, next) =>
                setForm((prev) => ({ ...prev, user: next }))
              }
              getOptionLabel={(option) =>
                option.name ? userOptionLabel(option) : ''
              }
              isOptionEqualToValue={(option, value) =>
                String(option._id) === String(value?._id)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Signs in as (seeded user)"
                  required
                  helperText="The tenant user this login lands in. Seeded ids are fixed, so the mapping survives a reset."
                />
              )}
            />
            <TextField
              label="Notes"
              value={form.notes}
              onChange={setField('notes')}
              fullWidth
              multiline
              minRows={2}
            />
            {dialog?.mode === 'edit' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        isActive: e.target.checked,
                      }))
                    }
                  />
                }
                label={form.isActive ? 'Enabled' : 'Disabled'}
              />
            )}
            {dialog?.mode === 'edit' && !form.isActive && (
              <Alert severity="warning">
                Disabling this account kills its active sessions immediately.
              </Alert>
            )}
            {dialogError && <Alert severity="error">{dialogError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving || !form.email || !form.password || !form.user}
            onClick={handleSave}
          >
            {saving
              ? 'Saving…'
              : dialog?.mode === 'create'
                ? 'Create Test Account'
                : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default TestAccounts
